import { useToken } from "@chakra-ui/react"
import { deleteCookie, hasCookie, setCookie } from "cookies-next"
import Script from "next/script"
import { useEffect } from "react"
import { lastSeenCookie } from "../../../common/constants/browser-storage"
import { cookieSolutionServices } from "../../../common/constants/constants"
import { getEnvSpecificKey } from "../../../common/utils/configuration-utils"
import { extractServicesFromStorage } from "../../../common/utils/cookie-solution-utils"

const CookieSolution = () => {
  const settingsId = getEnvSpecificKey(process.env.NEXT_PUBLIC_USERCENTRICS_CONFIG as string)

  const [brand500, brand700, brand800, black, baseWhite] = useToken("colors", [
    "brand.500",
    "brand.700",
    "brand.800",
    "black",
    "baseWhite",
  ])

  useEffect(() => {
    function handleConsentChange() {
      const currentServices = extractServicesFromStorage()

      const lastSeenCookieExists = hasCookie(lastSeenCookie.KEY)

      if (currentServices[cookieSolutionServices.GEBERIT_COMFORT]) {
        if (!lastSeenCookieExists) {
          setCookie(lastSeenCookie.KEY, "", { maxAge: lastSeenCookie.MAX_AGE })
        }
      } else {
        if (lastSeenCookieExists) {
          deleteCookie(lastSeenCookie.KEY)
        }
      }
    }

    window.addEventListener("ucConsentEvent", handleConsentChange)

    return () => {
      window.removeEventListener("ucConsentEvent", handleConsentChange)
    }
  }, [])

  return (
    settingsId && (
      <>
        <link rel="preconnect" href="//app.usercentrics.eu" />
        <link rel="preconnect" href="//api.usercentrics.eu" />
        <link rel="preload" href="//app.usercentrics.eu/browser-ui/latest/loader.js" as="script" />
        <link rel="dns-prefetch" href="//app.usercentrics.eu" />
        <link rel="dns-prefetch" href="//api.usercentrics.eu" />
        <Script
          id="usercentrics-cmp"
          data-settings-id={settingsId}
          src="https://app.usercentrics.eu/browser-ui/latest/loader.js"
          async
        ></Script>
        <Script id="usercentrics-cmp-configure">
          {`(function () {
          // switch mode
          const mode = "geberit-default";
          // set default colour
          let primaryColor = "${brand500}"; // use default from Usercentrics if not changed
          let acceptBtnBorderColor = "${brand700}"; // needs to be defined - not available in UC backend
          let acceptBtnFontColor = "${baseWhite}"; // use default from Usercentrics if not changed
          let acceptBtnHoverBgColor = "${brand800}"; // needs to be defined - not available in UC backend
          let acceptBtnHoverBgBorderColor = "${brand800}"; // needs to be defined - not available in UC backend
          let secondLayerTabColor = "${brand800}"; // use default from Usercentrics if not changed
          let secondLayerTabBorderColor = "${brand800}"; // needs to be defined - not available in UC backend
          let activeToggleFillColor = "${brand800}"; // use default from Usercentrics if not changed
          let saveBtnActiveColor = "${black}"; // needs to be defined - not available in UC backend

          const customStyle = \`
              :host(#usercentrics-root) div[data-testid="uc-buttons-container"], :host(#usercentrics-root) div[data-testid="first-line-buttons"] {justify-content: flex-end;}
              :host(#usercentrics-root) button[data-testid="uc-save-button"] {
                      flex: 0;
                  white-space: pre;
                  cursor: pointer;
                  font-weight: 500;
                  font-family: AktivGrotesk, helvetica, arial, sans-serif;
                  border: 1px solid #000000;
                  line-height: 1.25rem;
                  font-size: 0.875rem;
                  height: 2.5rem;
                  padding: 0.3125rem 1.5rem;
              }
              :host(#usercentrics-root) button[data-testid="uc-save-button"]:hover {
                  background-color: rgba(0,0,0,0.08);
                  border: 1px solid #000000;
              }
              :host(#usercentrics-root) button[data-testid="uc-save-button"]:active {
                  background-color: $\{saveBtnActiveColor};
                  border: 1px solid $\{saveBtnActiveColor};
                  color: #FFFFFF;
              }
              :host(#usercentrics-root) button[data-testid="uc-accept-all-button"] {
                  flex: 0;
                  white-space: pre;
                  cursor: pointer;
                  font-weight: 500;
                  font-family: AktivGrotesk, helvetica, arial, sans-serif;
                  $\{primaryColor ? "background-color: " + primaryColor + ";" : ""}
                  $\{acceptBtnFontColor ? "color: " + acceptBtnFontColor + ";" : ""}
                  border: 1px solid $\{acceptBtnBorderColor};
                  line-height: 1.25rem;
                  font-size: 0.875rem;
                  height: 2.5rem;
                  padding: 0.3125rem 1.5rem;
              }
              :host(#usercentrics-root) button[data-testid="uc-accept-all-button"]:active, :host(#usercentrics-root) button[data-testid="uc-accept-all-button"]:hover {
                  background-color: $\{acceptBtnHoverBgColor};
                  border: 1px solid $\{acceptBtnHoverBgBorderColor};
              }
              :host(#usercentrics-root) #uc-tab-list {
                  border-bottom: none;
                  padding-bottom: .5em;
              }
              :host(#usercentrics-root) #uc-tab-list button {
                  border-bottom-width: 0.25rem;
                  width: auto;
                  border-bottom: 0.25rem solid transparent;
                  height: 2.5rem;
              }
              :host(#usercentrics-root) #uc-tab-list button:first-child { margin-right: 1rem }
              :host(#usercentrics-root) #uc-tab-list button[aria-selected="true"] {
                  $\{secondLayerTabBorderColor ? "border-bottom-color: " + secondLayerTabBorderColor + ";" : ""}
                  $\{secondLayerTabColor ? "color: " + secondLayerTabColor + ";" : ""}
              }
              :host(#usercentrics-root) #uc-tab-list button[aria-selected="false"]:hover { border-bottom: 0.25rem solid rgb(180, 180, 180) }
              :host(#usercentrics-root) div[data-testid="uc-heading-title"] {
                  font-size: 26px;
                  font-weight: 600;
                  line-height: 30px;
              }
              :host(#usercentrics-root) div[data-testid="uc-headinger-wrapper"] {
                  padding: 0;
              }
              :host(#usercentrics-root) a, :host(#usercentrics-root), button[data-testid="uc-customize-anchor"] {
                  text-decoration: underline;
              }
              :host(#usercentrics-root) #uc-center-container {
                  margin: 30px auto 0;
                  padding: 1.5rem 0.5rem 1rem 0.5rem;
                  max-width: 750px;
                  width: 75%;
              }
              :host(#usercentrics-root) div[data-testid^="uc-tab-"] {
                  height: 2.5rem;
              }
          \`;

          const target = document.querySelector("html");
          const obsOptions = {
              childList: true,
              attributes: false,
              subtree: true,
          }
          let cb = (mutations, observer) => {
              if (document.querySelector('#usercentrics-root')) {
                  observer.disconnect();
                  const styleElement = document.createElement('style');
                  styleElement.innerHTML = customStyle;
                  try{
                      // chrome
                      document.querySelector('#usercentrics-root').shadowRoot.appendChild(styleElement);
                  }catch(e){};
                  // add mods to display toggles in correct color if changed
                  try{
                      const sroot = document.querySelector("#usercentrics-root").shadowRoot.querySelector("[data-testid='uc-app-container']");
                      let cb2 = (mutations, observer) => {
                          // try to change colors - do not kill observer in because layer can be opened multiple times
                          try{
                              if(activeToggleFillColor){
                                  let paths = sroot.querySelectorAll("button[aria-disabled='false'][aria-checked='true'] svg > g > g > g > path:nth-child(1)");
                                  paths.forEach(path => path.setAttribute("fill", activeToggleFillColor));
                              }
                          }catch(e){}
                          // remove UC Branding
                          try {
                              document.getElementById('usercentrics-root').shadowRoot.querySelector('#uc-center-container a[href*="usercentrics.com"]').closest('div').remove();
                          }catch(e){}
                      }
                      const obs2 = new MutationObserver(cb2);
                      obs2.observe(sroot,obsOptions);
                  }catch(e){}
              }
          }
          const obs = new MutationObserver(cb);
          obs.observe(target, obsOptions);
        })();`}
        </Script>
      </>
    )
  )
}

export default CookieSolution
